import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';

// Base URL from environment or default
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

// Create axios instance with cookie credentials support
export const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  // CRITICAL: Enable credentials to send/receive HttpOnly cookies
  // This is essential for cookie-based authentication
  withCredentials: true,
});

// Request interceptor - No longer needed for cookie-based auth
// Cookies are automatically sent with every request when withCredentials: true
// Kept for potential future use (e.g., logging, request ID tracking)
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // With cookie-based authentication, the JWT token is automatically
    // sent via HttpOnly cookie - no need to add Authorization header
    // The cookie is managed by the browser and cannot be accessed by JavaScript

    // Legacy code removed:
    // const token = localStorage.getItem('auth_token');
    // if (token && config.headers) {
    //   config.headers.Authorization = `Bearer ${token}`;
    // }

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle errors globally
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error: AxiosError) => {
    // Handle 401 Unauthorized - redirect to login
    // Cookie is automatically cleared by browser after logout endpoint call
    if (error.response?.status === 401) {
      // No need to clear localStorage - we're using HttpOnly cookies
      // The cookie will be cleared by calling /api/auth/logout
      // Don't redirect if already on login page or checking auth status
      const isAuthCheck = error.config?.url?.includes('/auth/me');
      const isOnLoginPage = window.location.pathname === '/login';
      if (!isAuthCheck && !isOnLoginPage) {
        window.location.href = '/login';
      }
    }

    // Handle 403 Forbidden
    if (error.response?.status === 403) {
      console.error('Accesso negato');
    }

    // Handle 500 Server Error
    if (error.response?.status === 500) {
      console.error('Errore del server');
    }

    // Handle network errors
    if (error.message === 'Network Error') {
      console.error('Errore di connessione. Verifica la tua connessione internet.');
    }

    return Promise.reject(error);
  }
);

// DEPRECATED: Helper functions for localStorage token management
// These are kept for backward compatibility but should not be used
// with cookie-based authentication
/**
 * @deprecated Use cookie-based authentication instead
 * Token is now stored in HttpOnly cookie for security
 */
export const setAuthToken = (_token: string | null) => {
  console.warn('setAuthToken is deprecated - using HttpOnly cookies instead');
  // No-op: token is managed via HttpOnly cookie
};

/**
 * @deprecated Use cookie-based authentication instead
 * Token is now stored in HttpOnly cookie and not accessible from JavaScript
 */
export const getAuthToken = (): string | null => {
  console.warn('getAuthToken is deprecated - token stored in HttpOnly cookie');
  // Cannot access HttpOnly cookie from JavaScript
  return null;
};

// Export default
export default api;
